import { useRef, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Divider, Input, Select, Space } from 'antd';
import { useSelector } from "react-redux";
import utils from './utils';
import { APIClient } from "../helpers/api_helper";
import { useDispatch } from 'react-redux';
import { loadTags } from '../slices/thunk';


const api = new APIClient();

// One row of the tag dropdown: indent by depth, colour swatch, title.
const renderTagOption = option => (
  <span style={{ paddingLeft: (option.data?.level || 0) * 16 }}>
    {option.data?.color_hex && (
      <span
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: 2,
          marginRight: 6,
          background: option.data.color_hex,
        }}
      />
    )}
    {option.data?.title}
  </span>
);


const TagsSelect = ({ value = [], onChange, multiple = false, size = null, minWidth = 200, allowCreate = true, placeholder = "Izberi oznako", excludeIds = [], allowNoValue = false, defaultOpen = false, autoFocus = false, popupMatchSelectWidth }) => {

  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [open, setOpen] = useState(defaultOpen);
  const [name, setName] = useState('');
  const [newParent, setNewParent] = useState(null);
  const [creating, setCreating] = useState(false);

  const excludeSet = new Set(excludeIds);
  const baseOpts = utils.buildTagOptions(useSelector(state => state.Tags.list))
    .filter(opt => !excludeSet.has(opt.value));
  const baseById = utils.processBackendOptsById(useSelector(state => state.Tags.byId))

  // Opt-in "N/A" pseudo-option = rows with NO tag. FILTERS ONLY: it must never
  // appear on data-entry forms, where it would become an assignable value.
  const noValue = utils.noValueOption();
  // level: 0 keeps it flush left, above the tag tree; no color_hex -> no swatch.
  const opts = allowNoValue ? [noValue, ...baseOpts] : baseOpts;
  // Also in the by-id map, or the chip for `tags_filters=none` renders blank.
  const tagsById = allowNoValue ? { ...baseById, [noValue.value]: noValue } : baseById;

  // Which existing tags may parent the tag being created. Built from baseOpts,
  // never `opts`: the N/A pseudo-option is not a real tag and must never become
  // a parent.
  const parentOpts = utils.parentableTagOptions(baseOpts);

  const resetCreator = () => {
    setName('');
    setNewParent(null);
  };

  const createAndSelectNew = (e) => {

    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || creating) return;

    // Tag titles are NOT unique in the DB (the Buxfer importer depends on
    // duplicates being legal), so this is a convenience rather than a
    // constraint: if that exact tag already sits under the chosen parent, pick
    // it instead of quietly creating a twin the user cannot tell apart.
    const existing = baseOpts.find(
      opt =>
        (opt.title || '').trim().toLowerCase() === trimmed.toLowerCase() &&
        (opt.parent_id ?? null) === (newParent ?? null)
    );
    if (existing) {
      resetCreator();
      setOpen(false);
      onChange(existing.value);
      return;
    }

    setCreating(true);
    api.post(
      '/api/tags/',
      { title: trimmed, parent: newParent ?? null },
      { successMsg: `Oznaka ${trimmed} uspešno ustvarjena!` }
    )
      .then(resp => {
        // A 403 resolves to null instead of rejecting (see api_helper), and
        // onChange(undefined) would silently blank the field.
        if (!resp?.id) return;
        resetCreator();
        setOpen(false);
        onChange(resp.id)
        dispatch(loadTags());  // so redux (app-wide) tags are updated!
      })
      // Leave the text and the parent in place on failure so the validation
      // message (shown as a toast) can actually be acted on.
      .catch(err => console.error('Unable to create tag!', err))
      .finally(() => setCreating(false));
  };

  // When the value is set from outside, sometimes its an int[], sometimes just int
  // ALSO, value in the <Select> needs to be full object {'label': 'xoxo', value: 5} (and not just id)
  const selectedValues = Array.isArray(value)
      ? value.map(x => tagsById[x])
      : tagsById[value];

  return (
    <Select
      open={open}
      autoFocus={autoFocus}
      popupMatchSelectWidth={popupMatchSelectWidth}
      size={size}
      onDropdownVisibleChange={(next) => {
        setOpen(next);
        // Don't leave a half-typed tag waiting to ambush the next open.
        if (!next) resetCreator();
      }}
      mode={multiple ? "tags" : undefined}
      showSearch
      style={{ minWidth: minWidth, flex: 1}}
      placeholder={placeholder}
      optionFilterProp="label"
      value={selectedValues} // <-- this must be array of strings (values)
      onChange={onChange}
      options={opts}
      optionRender={renderTagOption}
      allowClear
      dropdownRender={allowCreate ? (menu => (
        <>
          {menu}
          <Divider style={{ margin: '8px 0' }} />
          {/* Vertical: this footer also renders inside narrow pickers (the
              staging table's is 160px), where a single row would collapse. */}
          <Space direction="vertical" size={4} style={{ width: '100%', padding: '0 8px 8px' }}>
            <Input
              placeholder="Vnesi novo oznako"
              ref={inputRef}
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.stopPropagation()}
              onPressEnter={createAndSelectNew}
            />
            <Select
              placeholder="Nadrejena oznaka (neobvezno)"
              value={newParent}
              onChange={setNewParent}
              options={parentOpts}
              optionRender={renderTagOption}
              optionFilterProp="label"
              showSearch
              allowClear
              style={{ width: '100%' }}
              // Render this popup INSIDE the outer dropdown. Left on the body it
              // steals focus from the outer Select, which closes the whole
              // dropdown the moment you try to pick a parent.
              getPopupContainer={triggerNode => triggerNode.parentNode}
              onKeyDown={e => e.stopPropagation()}
            />
            <Button
              type="text"
              icon={<PlusOutlined />}
              onClick={createAndSelectNew}
              loading={creating}
              disabled={!name.trim()}
              block
            >
              Ustvari novo oznako
            </Button>
          </Space>
        </>
      )) : undefined}
    />
  );
};

export default TagsSelect;
